import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { z } from "zod";
import { dateSchema } from "./date-and-time";

export const patientNoteStatusSchema = z.enum([
  "initiated",
  "succeeded",
  "failed",
  "accepted",
]);

export type IPatientNoteStatus = z.infer<typeof patientNoteStatusSchema>;

export type ICreatePatientNote = z.infer<typeof _createPatientNoteSchema>;
export type IConvertedPatientNoteWithPatientId = z.infer<
  typeof convertedPatientNoteWithPatientIdSchema
>;

export const patientNoteTypeSchema = z.enum([
  "TransferNote",
  "Epicrisis",
  "GeneralNote",
]);

type IPatientNoteType = z.infer<typeof patientNoteTypeSchema>;
export const patientNoteTypes = patientNoteTypeSchema.options;

export const typeDictionary: Record<IPatientNoteType, MessageDescriptor> = {
  TransferNote: msg`Överflyttningsanteckning`,
  Epicrisis: msg`Epikris`,
  GeneralNote: msg`Daganteckning`,
};

const transferNoteSchema = z.object({
  type: z.literal(patientNoteTypeSchema.enum.TransferNote),
  note: z.object({
    diagnosis: z.string().nullish(),
    background: z.string().nullish(),
    current: z.string().nullish(),
  }),
});

const epicrisisSchema = z.object({
  type: z.literal(patientNoteTypeSchema.enum.Epicrisis),
  note: z.object({
    diagnosis: z.string().nullish(),
    courseOfCare: z.string().nullish(),
    reasonForAdmittance: z.string().nullish(),
    medicalHistory: z.string().nullish(),
  }),
});

const generalNoteSchema = z.object({
  type: z.literal(patientNoteTypeSchema.enum.GeneralNote),
  note: z.object({
    current: z.string().nullish(),
  }),
});

const patientNoteTypeAndNoteSchema = z.discriminatedUnion("type", [
  epicrisisSchema,
  transferNoteSchema,
  generalNoteSchema,
]);

const _createPatientNoteSchema = z.object({
  patientId: z.uuid(),
  anonymisedNote: patientNoteTypeAndNoteSchema,
  status: patientNoteStatusSchema.nullish(),
});

export const patientNoteStates = {
  EDITED: "edited",
  MUTATED: "mutated",
  SUBMITTED: "submitted",
  CANCEL_EDITING: "cancel_editing",
};

const convertedPatientNoteWithPatientIdSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  status: patientNoteStatusSchema,
  acceptedBy: z.string().nullish(),
  convertedText: z.string(),
  anonymisedNote: patientNoteTypeAndNoteSchema,
  conversionTimeStamp: dateSchema.nullish(),
});

export const convertedPatientNoteSchema =
  convertedPatientNoteWithPatientIdSchema.omit({
    patientId: true,
  });

type TransferNoteNoteKeys = keyof z.infer<typeof transferNoteSchema>["note"];
type EpicrisisNoteNoteKeys = keyof z.infer<typeof epicrisisSchema>["note"];
type GeneralNoteNoteKeys = keyof z.infer<typeof generalNoteSchema>["note"];

export type IPatientNoteNoteKeys =
  | TransferNoteNoteKeys
  | EpicrisisNoteNoteKeys
  | GeneralNoteNoteKeys;

export const noteByTypeDictionary: Record<
  IPatientNoteType,
  Partial<Record<IPatientNoteNoteKeys, MessageDescriptor>>
> = {
  TransferNote: {
    diagnosis: msg`Diagnos/Åtgärd`,
    background: msg`Bakgrund`,
    current: msg`Aktuellt`,
  },
  Epicrisis: {
    diagnosis: msg`Diagnos/Åtgärd`,
    courseOfCare: msg`Vårdförlopp`,
    reasonForAdmittance: msg`Inskrivningsorsak`,
    medicalHistory: msg`Anamnes`,
  },
  GeneralNote: {
    current: msg`Anteckning`,
  },
};
